import React from 'react';
import { Button } from './ui/button';
import { ExternalLink, ShieldCheck, TrendingUp, MapPin, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { LiveCounter } from './LiveCounter';

const SHARE_REGISTER_URL = 'https://www.das-aktienregister.ch/loewenherz-pool-invest-ag/';
const SWISS_BG = 'https://customer-assets.emergentagent.com/job_aktien-portal/artifacts/oxt9qeb8_Background%20loewenherz%20start%20page.png';

const TEXT = {
  de: {
    eyebrow: 'Schweizer Aktienregister',
    title: 'Werden Sie Aktionär der',
    titleHighlight: 'Löwenherz Invest AG',
    subtitle: 'Beteiligen Sie sich direkt an unserer Schweizer Immobiliengesellschaft. Transparent, reguliert und dauerhaft im offiziellen Aktienregister geführt.',
    benefit1Title: 'Schweizer Sicherheit',
    benefit1Desc: 'AG mit Sitz in der Schweiz, vollständig regulierter Rechtsrahmen.',
    benefit2Title: 'Transparente Verwaltung',
    benefit2Desc: 'Alle Aktien sind im offiziellen Schweizer Aktienregister hinterlegt.',
    benefit3Title: 'Direkte Beteiligung',
    benefit3Desc: 'Profitieren Sie unmittelbar an Wertentwicklung und Erträgen.',
    ctaPrimary: 'Jetzt als Investor registrieren',
    ctaSecondary: 'Mehr erfahren',
    note: 'Externer Partner · das-aktienregister.ch'
  },
  en: {
    eyebrow: 'Swiss Share Register',
    title: 'Become a shareholder of',
    titleHighlight: 'Löwenherz Invest AG',
    subtitle: 'Invest directly in our Swiss real estate company. Transparent, regulated and permanently registered in the official Swiss share register.',
    benefit1Title: 'Swiss Security',
    benefit1Desc: 'Stock corporation based in Switzerland, fully regulated legal framework.',
    benefit2Title: 'Transparent Administration',
    benefit2Desc: 'All shares are recorded in the official Swiss share register.',
    benefit3Title: 'Direct Participation',
    benefit3Desc: 'Benefit immediately from value growth and returns.',
    ctaPrimary: 'Register as investor',
    ctaSecondary: 'Learn more',
    note: 'External partner · das-aktienregister.ch'
  },
  ru: {
    eyebrow: 'Швейцарский реестр акций',
    title: 'Станьте акционером',
    titleHighlight: 'Löwenherz Invest AG',
    subtitle: 'Инвестируйте напрямую в нашу швейцарскую компанию недвижимости. Прозрачно, регулируемо и постоянно учтено в официальном швейцарском реестре акций.',
    benefit1Title: 'Швейцарская надёжность',
    benefit1Desc: 'Акционерное общество в Швейцарии, полностью регулируемая правовая база.',
    benefit2Title: 'Прозрачное управление',
    benefit2Desc: 'Все акции зарегистрированы в официальном швейцарском реестре.',
    benefit3Title: 'Прямое участие',
    benefit3Desc: 'Сразу получайте выгоду от роста стоимости и доходности.',
    ctaPrimary: 'Зарегистрироваться как инвестор',
    ctaSecondary: 'Узнать больше',
    note: 'Внешний партнёр · das-aktienregister.ch'
  },
  vi: {
    eyebrow: 'Sổ đăng ký cổ đông Thụy Sĩ',
    title: 'Trở thành cổ đông của',
    titleHighlight: 'Löwenherz Invest AG',
    subtitle: 'Đầu tư trực tiếp vào công ty bất động sản Thụy Sĩ của chúng tôi. Minh bạch, được quản lý và được lưu trữ vĩnh viễn trong sổ đăng ký cổ đông chính thức của Thụy Sĩ.',
    benefit1Title: 'An toàn kiểu Thụy Sĩ',
    benefit1Desc: 'Công ty cổ phần có trụ sở tại Thụy Sĩ, khung pháp lý được quản lý đầy đủ.',
    benefit2Title: 'Quản lý minh bạch',
    benefit2Desc: 'Tất cả cổ phiếu được ghi nhận trong sổ đăng ký cổ đông chính thức của Thụy Sĩ.',
    benefit3Title: 'Tham gia trực tiếp',
    benefit3Desc: 'Hưởng lợi ngay từ sự tăng trưởng giá trị và lợi nhuận.',
    ctaPrimary: 'Đăng ký với tư cách nhà đầu tư',
    ctaSecondary: 'Tìm hiểu thêm',
    note: 'Đối tác bên ngoài · das-aktienregister.ch'
  }
};

export const Aktienregister = () => {
  const { language } = useLanguage();
  const text = TEXT[language] || TEXT.de;

  const benefits = [
    { icon: ShieldCheck, title: text.benefit1Title, desc: text.benefit1Desc },
    { icon: MapPin, title: text.benefit2Title, desc: text.benefit2Desc },
    { icon: TrendingUp, title: text.benefit3Title, desc: text.benefit3Desc }
  ];

  return (
    <section
      id="aktienregister"
      data-testid="aktienregister-section"
      className="relative overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${SWISS_BG})` }}
        aria-hidden
      />
      {/* Cinematic overlays for legibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" aria-hidden />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-24 md:py-32">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#C4A55A]/15 border border-[#C4A55A]/40 rounded-full mb-6 backdrop-blur-sm">
            <span className="h-2 w-2 rounded-full bg-[#C4A55A] animate-pulse" />
            <span className="text-[#C4A55A] text-sm font-medium tracking-wide uppercase">
              {text.eyebrow}
            </span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {text.title}{' '}
            <span className="block bg-gradient-to-r from-[#D4B56A] via-[#C4A55A] to-[#B8974E] bg-clip-text text-transparent">
              {text.titleHighlight}
            </span>
          </h2>

          <p className="text-lg md:text-xl text-gray-200 mb-10 max-w-2xl drop-shadow-md">
            {text.subtitle}
          </p>

          {/* Benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={i}
                  className="bg-black/50 backdrop-blur-md border border-[#C4A55A]/25 rounded-xl p-5 hover:border-[#C4A55A]/60 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-lg bg-[#C4A55A]/15 border border-[#C4A55A]/40 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-[#C4A55A]" />
                  </div>
                  <h3 className="text-white font-semibold mb-1.5 text-base">
                    {b.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Button
              size="lg"
              data-testid="aktienregister-cta-primary"
              onClick={() => {
                const el = document.getElementById('investors');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold px-8 py-6 text-base shadow-lg shadow-[#C4A55A]/30 hover:shadow-[#C4A55A]/50 transition-all duration-300 hover:scale-[1.03] group"
            >
              {text.ctaPrimary}
              <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            <Button
              asChild
              variant="outline"
              size="lg"
              data-testid="aktienregister-cta-secondary"
              className="border-2 border-white/30 bg-black/30 backdrop-blur-sm text-white hover:bg-white hover:text-black px-8 py-6 text-base transition-all duration-300"
            >
              <a href={SHARE_REGISTER_URL} target="_blank" rel="noopener noreferrer">
                {text.ctaSecondary}
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-6 flex items-center gap-2">
            <ExternalLink className="h-3 w-3" />
            {text.note}
          </p>
        </div>

        {/* Live Counter (Phase 1) */}
        <div className="max-w-5xl">
          <LiveCounter />
        </div>
      </div>
    </section>
  );
};
